import { SmallIcon } from '@/components/Icon'
import { gemItemIcon } from '@/logic/gem'
import { WithGemTemplateTooltip } from '@/tooltip'
import { SkillsEmbed } from '@/types/embed'
import { LevelingEmbed } from '@/types/leveling'
import { GemAcquisition, loadGemAcquisition, questToVendorsMap } from '@/utils/gems'
import Data from 'data'
import React from 'react'
import Store from 'store'
import { EmbedRef, useEmbed } from '../../common'

function SkillGem({ id }: { id: string }) {
  const gem = Data.gems[id]
  if (!gem) return null
  const icon = gemItemIcon(gem)
  return (
    <WithGemTemplateTooltip id={id}>
      <div className="poe-item-gem">
        {!!icon && <SmallIcon icon={icon} />}
        {gem?.active_skill?.display_name || gem?.base_item?.display_name}
      </div>
    </WithGemTemplateTooltip>
  )
}

export function LevelingGemAquisition({
  embed,
  stores,
  activeSkills
}: {
  embed: LevelingEmbed
  stores: Record<string, Store>
  activeSkills:
    | EmbedRef<
        SkillsEmbed & {
          type: 'skills' | 'skills-video-display'
        }
      >
    | undefined
}) {
  // Used mostly for figuring out where to get the gems from
  const overviewAscendancy = useEmbed(stores, embed.overview.ascendancy, 'ascendancy')

  // Skills Tab
  const gemsUsed = React.useMemo(() => {
    const skillGems = new Set<string>()
    const skills = activeSkills?.embed.steps[activeSkills.active ?? activeSkills.embed.step]?.skills || []
    for (const links of skills) {
      for (const gem of links.gems) {
        skillGems.add(gem.id)
      }
    }

    return Array.from(skillGems)
  }, [activeSkills])

  const [gemAcquisitionData, setGemAcquisitionData] = React.useState<Record<string, GemAcquisition>>()
  React.useEffect(() => {
    let mounted = true
    loadGemAcquisition().then((response) => {
      if (mounted) setGemAcquisitionData(response)
    })
    return () => {
      mounted = false
    }
  }, [])

  const gemAcquisition = React.useMemo(() => {
    if (!overviewAscendancy || !gemAcquisitionData) return
    return gemsUsed.map((gem) => {
      const gemResult: {
        id: string
        questReward?: string
        questVendorReward?: string
      } = { id: gem }

      const gemAquisitionData = gemAcquisitionData[gem]
      const charClass = Data.ascendancyClass[overviewAscendancy.embed.ascendancy] ?? 'StrDexInt'
      if (gemAquisitionData?.[charClass]?.includes('Q')) {
        gemResult.questReward = gemAquisitionData['Q']
      }
      if (gemAquisitionData?.[charClass]?.includes('V')) {
        gemResult.questVendorReward = gemAquisitionData['V']
      }

      return gemResult
    })
  }, [gemsUsed, gemAcquisitionData, overviewAscendancy])

  return (
    <div title="Gem Aquisition" className="poe-gem-aquisition">
      {gemAcquisition && (
        <ul>
          {gemAcquisition.map((gem, index) => {
            return gem.questReward || gem.questVendorReward ? (
              <li key={index}>
                <SkillGem id={gem.id} />
                <span>
                  Quest: <strong>{gem.questReward || gem.questVendorReward}</strong>
                </span>
                <span>
                  Aquire:{' '}
                  {gem.questReward && (
                    <>
                      as <strong>reward</strong>
                    </>
                  )}
                  {gem.questReward && gem.questVendorReward && ' OR '}
                  {gem.questVendorReward && (
                    <>
                      buy from <strong>{questToVendorsMap[gem.questVendorReward.split('- ')[1]]}</strong>
                    </>
                  )}
                </span>
              </li>
            ) : null
          })}
        </ul>
      )}
    </div>
  )
}
